import React from "react";
import classNames from "classnames";
import { appFeatureFlags } from "@app/config/features/appFeatureFlags";
import { Button } from "../button";
import { COMMON_ICONS, Icon } from "../icon";
import "./FormFooter.scss";

/** Пропсы компактного футера формы/окна. */
export interface FormFooterProps {
    /** Основное perform-действие формы: сохранить, добавить, применить. */
    onConfirm?: () => void;
    /** Optional dismiss action для экранов без window shell/back affordance. */
    onCancel?: () => void;
    /** Текст основной кнопки. */
    confirmText?: string;
    /** Текст optional dismiss-кнопки. */
    cancelText?: string;
    /** Показывает загрузку основной кнопки и блокирует клик. */
    confirmLoading?: boolean;
    /** Блокирует основную кнопку. */
    confirmDisabled?: boolean;
    /** Блокирует optional dismiss-кнопку. */
    cancelDisabled?: boolean;
    /** Слот перед текстом основной кнопки. */
    confirmStartSlot?: React.ReactNode;
    /** Низкоприоритетное действие перед основной кнопкой: например `Сбросить`. */
    leadingAction?: React.ReactNode;
    /**
     * @deprecated Используйте `leadingAction`. Слот сохранён только для плавной миграции старых вызовов.
     */
    leftActions?: React.ReactNode;
    /** Дополнительный CSS-класс корневого элемента. */
    className?: string;
}

/** Пропсы футера фильтра. */
export interface FormFooterFilterProps extends Omit<FormFooterProps, "confirmStartSlot" | "leadingAction" | "leftActions"> {
    /** Сбрасывает значения фильтра к пустому состоянию. */
    onReset: () => void;
    /** Текст кнопки сброса. */
    resetText?: string;
    /** Блокирует кнопку сброса, пока форма не готова к взаимодействию. */
    resetDisabled?: boolean;
}

/**
 * Компактный footer overlay-формы для ТСД.
 * Основной сценарий — одна perform-кнопка; закрытие окна живёт в shell (`x`/`Назад`).
 */
export const FormFooter: React.FC<FormFooterProps> = ({
    onConfirm,
    onCancel,
    confirmText = "Подтвердить",
    cancelText = "Отмена",
    confirmLoading = false,
    confirmDisabled = false,
    cancelDisabled = false,
    confirmStartSlot,
    leadingAction,
    leftActions,
    className,
}) => {
    const resolvedLeadingAction = leadingAction ?? leftActions;

    return (
        <div className={classNames("shared_form_footer", className)}>
            {resolvedLeadingAction != null && <div className="shared_form_footer__leading">{resolvedLeadingAction}</div>}
            <div className="shared_form_footer__actions">
                {onCancel != null && (
                    <Button variant="secondary" disabled={cancelDisabled} onClick={onCancel} fullWidth={true}>
                        {cancelText}
                    </Button>
                )}
                {onConfirm != null && (
                    <Button
                        variant="primary"
                        loading={confirmLoading}
                        disabled={confirmDisabled}
                        startSlot={confirmStartSlot}
                        onClick={onConfirm}
                        fullWidth={true}
                    >
                        {confirmText}
                    </Button>
                )}
            </div>
        </div>
    );
};

/**
 * Футер окна фильтра: сброс значений + применение фильтра.
 */
const FormFooterFilter: React.FC<FormFooterFilterProps> = ({
    onReset,
    confirmText = "Поиск",
    resetText = "Сбросить",
    resetDisabled = false,
    ...rest
}) => {
    const confirmIcon = appFeatureFlags.showFilterPrimaryActionIcon ? (
        <Icon src={COMMON_ICONS.search} size="md" />
    ) : undefined;

    return (
        <FormFooter
            confirmText={confirmText}
            {...rest}
            leadingAction={
                <Button
                    variant="secondary"
                    disabled={resetDisabled}
                    startSlot={<Icon src={COMMON_ICONS.reset} size="md" />}
                    onClick={onReset}
                    fullWidth={true}
                >
                    {resetText}
                </Button>
            }
            confirmStartSlot={confirmIcon}
        />
    );
};

const FormFooterCompound = Object.assign(FormFooter, { Filter: FormFooterFilter });
export default FormFooterCompound;
